import chroma from 'chroma-js';

import type { TBrandRamp, TDarkBrandRamp } from './types';

// Calibrated so the Glomo seed (#153298) resolves to blue.700/50/100. hover/active
// darken relative to the seed; subtle/muted use an absolute OKLCH lightness so a
// light seed still yields a pale tint rather than clamping to white.
const HOVER_LIGHTNESS_DELTA = -0.0524;
const HOVER_CHROMA_SCALE = 0.831;
const SUBTLE_LIGHTNESS = 0.9467;
const SUBTLE_CHROMA_SCALE = 0.123;
const MUTED_LIGHTNESS = 0.8925;
const MUTED_CHROMA_SCALE = 0.254;

const rampCache = new Map<string, TBrandRamp>();

export const generateBrandRamp = (seedHex: string): TBrandRamp => {
  const cached = rampCache.get(seedHex);
  if (cached) return cached;

  const seed = chroma(seedHex);
  const [lightness, chromaValue, hue] = seed.oklch();
  const safeHue = Number.isNaN(hue) ? 0 : hue;

  const ramp: TBrandRamp = {
    primary: seed.hex(),
    hover: chroma.oklch(lightness + HOVER_LIGHTNESS_DELTA, chromaValue * HOVER_CHROMA_SCALE, safeHue).hex(),
    subtle: chroma.oklch(SUBTLE_LIGHTNESS, chromaValue * SUBTLE_CHROMA_SCALE, safeHue).hex(),
    muted: chroma.oklch(MUTED_LIGHTNESS, chromaValue * MUTED_CHROMA_SCALE, safeHue).hex(),
  };

  rampCache.set(seedHex, ramp);
  return ramp;
};

// Dark-surface stops, calibrated so the Glomo seed resolves to the dark brand tokens: blue-400 fill,
// blue-500 hover, blue-300 text/lines and blue-200 interactive hover. Lightness is absolute so any
// seed lands at the same contrast against the dark surfaces; chroma scales from the seed.
const DARK_STOPS = {
  primary: { lightness: 0.5541, chromaScale: 1.0585 },
  hover: { lightness: 0.4668, chromaScale: 1.1871 },
  text: { lightness: 0.6681, chromaScale: 0.8026 },
  textHover: { lightness: 0.7886, chromaScale: 0.4969 },
} as const;

// neutral-900 / neutral-800 / neutral-950: bg-default, bg-elevated and bg-subtle in dark.
const DARK_SURFACES = ['#0f172a', '#1e293b', '#020617'];

const darkRampCache = new Map<string, TDarkBrandRamp>();

export const generateDarkBrandRamp = (seedHex: string): TDarkBrandRamp => {
  const cached = darkRampCache.get(seedHex);
  if (cached) return cached;

  const [, chromaValue, hue] = chroma(seedHex).oklch();
  const safeHue = Number.isNaN(hue) ? 0 : hue;
  const stop = ({ lightness, chromaScale }: { lightness: number; chromaScale: number }) =>
    chroma.oklch(lightness, chromaValue * chromaScale, safeHue).hex();

  // Saturated hues (violet, red) read darker than their OKLCH lightness suggests, so lift the text
  // stop until it clears 4.5:1 on every dark surface.
  let text = stop(DARK_STOPS.text);
  for (let lift = 0.01; lift <= 0.1 && DARK_SURFACES.some((surface) => chroma.contrast(text, surface) < 4.5); lift += 0.01) {
    text = stop({ ...DARK_STOPS.text, lightness: DARK_STOPS.text.lightness + lift });
  }

  const ramp: TDarkBrandRamp = {
    primary: stop(DARK_STOPS.primary),
    hover: stop(DARK_STOPS.hover),
    text,
    textHover: stop(DARK_STOPS.textHover),
  };

  darkRampCache.set(seedHex, ramp);
  return ramp;
};
